import { useDispatch, useStore } from "react-redux";

import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import { setSpatialConfig } from "@/store/slice/spatialSlice";

import type { AnalysisConfiguration } from "@lokasi-ai-web-sdk/agent-ui";

import { toSpatialConfigPatch } from "./utils";

/**
 * Applies the output analysis the agent settled on — grid type, resolution,
 * scoring, coverage — to the app's spatial configuration.
 *
 * Settings only: S7 carries no geometry, so nothing paints from this. It writes
 * the same `spatialConfig` the Output Analysis panel writes, so the agent
 * becomes another writer of it and the result grids are later drawn the way the
 * conversation asked for.
 *
 * No ledger and no clearing, unlike the working area: there is nothing on the
 * map to take back off, the contract only ever adds or replaces this block, and
 * the initial configuration is not the value the user had before — so a
 * workspace without an output analysis leaves whatever is configured alone.
 */
export const useAgentOutputAnalysis = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const { isCoverageTypeEnabled } = useAppFeatures();

  const syncOutputAnalysis = (configuration: AnalysisConfiguration | null) => {
    const patch = toSpatialConfigPatch(
      configuration?.output_analysis,
      isCoverageTypeEnabled
    );

    if (!patch) return;

    // Read through the store rather than a selector: this runs inside the
    // adapter's asynchronous sync, where a value captured at render time can
    // already be a dataset add behind. `setSpatialConfig` merges, so the spread
    // only carries the keys this block does not own.
    dispatch(
      setSpatialConfig({ ...store.getState().spatial.spatialConfig, ...patch })
    );
  };

  return { syncOutputAnalysis };
};
